import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const SUPPORTED_LANGS = ["en", "hi", "kn", "ta", "ml", "te", "bn", "mr", "gu", "other"];
const empty = () => ({
  names: {}, image_url: "", product_ids: [], category_ids: [], sort: 0, active: true,
});

export default function AdminCollections() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  const load = async () => {
    const [c, p, cat] = await Promise.all([
      api.get("/admin/collections"),
      api.get("/admin/products"),
      api.get("/admin/categories"),
    ]);
    setItems(c.data); setProducts(p.data); setCategories(cat.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/admin/collections", form);
      else await api.patch(`/admin/collections/${editing}`, form);
      toast.success("Saved");
      setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this collection?")) return;
    await api.delete(`/admin/collections/${id}`); load();
  };

  const toggleId = (key, id) => {
    setForm((f) => ({
      ...f, [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id],
    }));
  };

  return (
    <div className="p-8 lg:p-12 max-w-6xl">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline">Curated rows</p>
          <h1 className="editorial-h text-5xl text-brand-dark mt-2">Collections</h1>
          <p className="font-sans text-sm text-brand-muted mt-2">Themed product rows shown to customers (e.g. Best Sellers, New Arrivals).</p>
        </div>
        <Button onClick={() => { setEditing("new"); setForm(empty()); }} data-testid="collection-new-btn" className="rounded-none bg-brand-primary text-brand-bone gap-2">
          <Plus strokeWidth={1.5} className="w-4 h-4" /> Add collection
        </Button>
      </div>

      {editing && (
        <form onSubmit={save} className="mt-8 bg-white border border-brand-line p-6 space-y-6" data-testid="collection-form">
          <div>
            <Label className="overline">Name (per language)</Label>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
              {SUPPORTED_LANGS.map((l) => (
                <div key={l}>
                  <p className="text-[10px] uppercase tracking-widest text-brand-muted">{l}</p>
                  <Input className="h-10 rounded-none border-brand-line" value={form.names[l] || ""} onChange={(e) => setForm({ ...form, names: { ...form.names, [l]: e.target.value } })} required={l === "en"} />
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="overline">Show on which concern cards</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {categories.map((cat) => {
                const sel = form.category_ids.includes(cat.id);
                return (
                  <button type="button" key={cat.id} onClick={() => toggleId("category_ids", cat.id)} className={`text-xs px-3 py-1.5 border ${sel ? "bg-brand-primary text-brand-bone border-brand-primary" : "border-brand-line"}`}>
                    {cat.labels?.en || cat.slug}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="overline">Products in collection</Label>
            <div className="grid sm:grid-cols-2 gap-2 mt-2 max-h-80 overflow-y-auto">
              {products.map((p) => {
                const sel = form.product_ids.includes(p.id);
                return (
                  <button type="button" key={p.id} onClick={() => toggleId("product_ids", p.id)} className={`p-2 border flex items-center gap-2 text-left ${sel ? "border-brand-primary bg-brand-primary/5" : "border-brand-line"}`}>
                    <img src={p.image_url} alt="" className="w-10 h-10 object-cover bg-brand-elevated" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-brand-dark truncate">{p.names?.en}</p>
                      <p className="text-xs text-brand-muted">₹{p.price}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            <Label className="font-sans text-sm">Active</Label>
          </div>

          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-none border-brand-line">Cancel</Button>
            <Button type="submit" className="rounded-none bg-brand-primary text-brand-bone" data-testid="collection-form-submit">Save</Button>
          </div>
        </form>
      )}

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => (
          <div key={c.id} className="bg-white border border-brand-line p-4">
            <p className="font-serif text-xl text-brand-dark">{c.names?.en}</p>
            <p className="text-xs text-brand-muted mt-1">{c.product_ids?.length || 0} products · {c.active ? "Active" : "Paused"}</p>
            <div className="flex gap-2 mt-3 justify-end">
              <button onClick={() => { setEditing(c.id); setForm({ names: c.names || {}, image_url: c.image_url || "", product_ids: c.product_ids || [], category_ids: c.category_ids || [], sort: c.sort || 0, active: c.active }); }} className="text-xs text-brand-dark hover:text-brand-primary flex items-center gap-1"><Pencil strokeWidth={1.5} className="w-3 h-3" /> Edit</button>
              <button onClick={() => remove(c.id)} className="text-xs text-brand-dark hover:text-brand-primary flex items-center gap-1"><Trash2 strokeWidth={1.5} className="w-3 h-3" /> Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
